import { useEffect, type RefObject } from 'react';

interface Options {
  length: number;
  selectedIndex: number;
  onSelect: (index: number) => void;
  onApprove?: (index: number) => void;
  onDeny?: (index: number) => void;
  onUndo?: () => void;
  /** Ref to the scrollable container — selected row will be scrolled into view. */
  containerRef?: RefObject<HTMLElement | null>;
  enabled?: boolean;
}

/**
 * Keyboard navigation for the approvals queue (R-76).
 * j/k: move focus, a: approve, d: deny, ⌘Z / Ctrl+Z: undo.
 * Disabled when focus is inside an input or textarea.
 */
export function useKeyboardNav({
  length,
  selectedIndex,
  onSelect,
  onApprove,
  onDeny,
  onUndo,
  containerRef,
  enabled = true,
}: Options) {
  useEffect(() => {
    if (!enabled || length === 0) return;

    function handler(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const isUndo =
        (e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey;

      if (isUndo) {
        e.preventDefault();
        onUndo?.();
        return;
      }

      switch (e.key) {
        case 'j':
        case 'ArrowDown': {
          e.preventDefault();
          const next = Math.min(selectedIndex + 1, length - 1);
          onSelect(next);
          scrollIntoView(containerRef, next);
          break;
        }
        case 'k':
        case 'ArrowUp': {
          e.preventDefault();
          const prev = Math.max(selectedIndex - 1, 0);
          onSelect(prev);
          scrollIntoView(containerRef, prev);
          break;
        }
        case 'a':
          e.preventDefault();
          onApprove?.(selectedIndex);
          break;
        case 'd':
          e.preventDefault();
          onDeny?.(selectedIndex);
          break;
      }
    }

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, length, selectedIndex, onSelect, onApprove, onDeny, onUndo, containerRef]);
}

function scrollIntoView(
  containerRef: RefObject<HTMLElement | null> | undefined,
  index: number,
) {
  const container = containerRef?.current;
  if (!container) return;
  const rows = container.querySelectorAll<HTMLElement>('[data-approval-row]');
  rows[index]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}
